/**
 * MediaUpload — the one control for putting a file into the app.
 *
 * Replaces every "paste a URL" box: pick from the device, take a photo with
 * the phone camera, or drag a file in. Shows a live preview, upload progress
 * and a Replace / Remove pair once something is stored. There is deliberately
 * no URL text field — media belongs to the app, not to somebody else's server.
 */
import React, { useRef, useState } from 'react';
import {
  Upload, Camera, X, Loader2, FileText, Film, Music, ImageIcon, RefreshCw,
  Images,
} from 'lucide-react';
import { toast } from 'sonner';

import { uploadMedia, acceptFor } from '@/lib/mediaUpload';
import { MediaLibraryPicker } from '@/components/media/MediaLibraryPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const absolute = (url) => (url && url.startsWith('/') ? `${API_URL}${url}` : url || '');
const isVideo = (url) => /\.(mp4|webm|mov|m4v|avi|mkv)(\?|$)/i.test(url || '');
const isAudio = (url) => /\.(mp3|wav|m4a|aac|ogg|flac)(\?|$)/i.test(url || '');
const isImage = (url) => /\.(png|jpe?g|webp|gif|svg|bmp|avif|heic|heif)(\?|$)/i.test(url || '');

const KindIcon = ({ url }) => {
  if (isVideo(url)) return <Film className="h-5 w-5" />;
  if (isAudio(url)) return <Music className="h-5 w-5" />;
  if (isImage(url)) return <ImageIcon className="h-5 w-5" />;
  return <FileText className="h-5 w-5" />;
};

export const MediaUpload = ({
  value,
  onChange,
  purpose = 'general',
  label,
  hint,
  testId = 'media-upload',
  className = '',
  previewClassName = 'h-28',
  disabled = false,
  allowCamera = true,
  onUploaded,
}) => {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [picking, setPicking] = useState(false);

  const accept = acceptFor(purpose);
  const cameraOffered = allowCamera && accept.includes('image');
  const preview = absolute(value);

  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    setProgress(0);
    try {
      const asset = await uploadMedia(file, { purpose, onProgress: setProgress });
      onChange(asset.url, asset);
      if (onUploaded) onUploaded(asset);
      toast.success(`${asset.filename} uploaded`);
    } catch (err) {
      toast.error(err.message || 'That file could not be uploaded');
    } finally {
      setBusy(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = '';
      if (cameraRef.current) cameraRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled || busy) return;
    send(e.dataTransfer.files?.[0]);
  };

  return (
    <div className={`space-y-2 ${className}`} data-testid={testId}>
      {label && (
        <span className="block text-xs font-medium text-slate-600">{label}</span>
      )}

      <input ref={fileRef} type="file" accept={accept} hidden
             onChange={(e) => send(e.target.files?.[0])}
             data-testid={`${testId}-input`} />
      {cameraOffered && (
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden
               onChange={(e) => send(e.target.files?.[0])}
               data-testid={`${testId}-camera-input`} />
      )}

      {value ? (
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3"
             data-testid={`${testId}-preview`}>
          {isImage(value) ? (
            <img src={preview} alt="" className={`${previewClassName} w-auto max-w-[180px]
                       rounded-md object-cover bg-slate-100`} />
          ) : isVideo(value) ? (
            <video src={preview} className={`${previewClassName} w-auto max-w-[180px] rounded-md bg-black`}
                   controls preload="metadata" />
          ) : (
            <span className="flex h-14 w-14 items-center justify-center rounded-md bg-slate-100 text-slate-500">
              <KindIcon url={value} />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs text-slate-500">
              {decodeURIComponent((value.split('/').pop() || '').replace(/^[a-f0-9]{12}-/, ''))}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" disabled={disabled || busy}
                      onClick={() => fileRef.current?.click()}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200
                                 px-2.5 py-1.5 text-xs font-medium text-slate-600
                                 transition-colors hover:bg-slate-50 disabled:opacity-50"
                      data-testid={`${testId}-replace`}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <RefreshCw className="h-3.5 w-3.5" />}
                Replace
              </button>
              <button type="button" disabled={disabled || busy}
                      onClick={() => setPicking(true)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200
                                 px-2.5 py-1.5 text-xs font-medium text-slate-600
                                 transition-colors hover:bg-slate-50 disabled:opacity-50"
                      data-testid={`${testId}-library-swap`}>
                <Images className="h-3.5 w-3.5" />
                Library
              </button>
              <button type="button" disabled={disabled || busy}
                      onClick={() => onChange('', null)}
                      className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5
                                 text-xs font-medium text-slate-500 transition-colors
                                 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                      data-testid={`${testId}-remove`}>
                <X className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
             onDragLeave={() => setDragging(false)}
             onDrop={onDrop}
             className={`rounded-lg border-2 border-dashed p-4 text-center transition-colors
                         ${dragging ? 'border-cyan-400 bg-cyan-50'
                                    : 'border-slate-200 bg-slate-50/60'}`}
             data-testid={`${testId}-dropzone`}>
          {busy ? (
            <div className="py-2" data-testid={`${testId}-progress`}>
              <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
              <div className="mx-auto mt-3 h-1.5 w-40 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full rounded-full bg-cyan-500 transition-all"
                     style={{ width: `${progress}%` }} />
              </div>
              <p className="mt-2 text-xs text-slate-500">Uploading… {progress}%</p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button type="button" disabled={disabled}
                        onClick={() => fileRef.current?.click()}
                        className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2
                                   text-xs font-medium text-white transition-colors
                                   hover:bg-slate-700 disabled:opacity-50"
                        data-testid={`${testId}-choose`}>
                  <Upload className="h-3.5 w-3.5" /> Upload from device
                </button>
                {cameraOffered && (
                  <button type="button" disabled={disabled}
                          onClick={() => cameraRef.current?.click()}
                          className="inline-flex items-center gap-2 rounded-md border border-slate-200
                                     bg-white px-3 py-2 text-xs font-medium text-slate-600
                                     transition-colors hover:bg-slate-50 disabled:opacity-50 sm:hidden"
                          data-testid={`${testId}-camera`}>
                    <Camera className="h-3.5 w-3.5" /> Take a photo
                  </button>
                )}
                <button type="button" disabled={disabled}
                        onClick={() => setPicking(true)}
                        className="inline-flex items-center gap-2 rounded-md border border-slate-200
                                   bg-white px-3 py-2 text-xs font-medium text-slate-600
                                   transition-colors hover:bg-slate-50 disabled:opacity-50"
                        data-testid={`${testId}-library`}>
                  <Images className="h-3.5 w-3.5" /> Choose from library
                </button>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">
                {hint || 'Drag a file here, or pick one from your phone, tablet or computer'}
              </p>
            </>
          )}
        </div>
      )}

      <MediaLibraryPicker
        open={picking}
        onOpenChange={setPicking}
        purpose={purpose}
        onPick={(asset) => {
          onChange(asset.url, asset);
          if (onUploaded) onUploaded(asset);
          toast.success(`Using ${asset.name}`);
        }}
      />
    </div>
  );
};

export default MediaUpload;
